import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { IonicModule } from '@ionic/angular';
import { HttpBackend, provideHttpClient, withInterceptors } from '@angular/common/http';
import { Drivers } from '@ionic/storage';
import { IonicStorageModule, StorageConfig } from '@ionic/storage-angular';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { SocketIoModule, SocketIoConfig } from 'ngx-socket-io';

// Environment
import { HashLocationStrategy, LocationStrategy } from '@angular/common';
import { environment } from '../environments/environment';

// Interceptors
import intercept from './core/interceptor/http-headers.interceptor';
import errorIntercept from './core/interceptor/error-handler.interceptor';

// Translations
import { TranslationService, createTranslateLoader } from './core/services/translation/translation.service';

// Services
import { StorageService } from './core/services/storage/storage.service';
import { CommonService } from './core/services/common/common.service';

// Custom Modules
import { CoreModule } from './core/core.module';
import { SharedModule } from './shared/shared.module';

// Types
import { StorageKey } from './shared/types/storage';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

const WEBSERVICES_MODE = environment.WEBSERVICES_MODE as 'ONLINE' | 'LOCAL';
const SOCKET_URL = environment.WEBSERVICES[WEBSERVICES_MODE].SOCKET_URL;

const config: SocketIoConfig = { url: SOCKET_URL, options: {} };

const storageConfig = {
  name: environment.STORAGE_NAME,
  driverOrder: [Drivers.IndexedDB, Drivers.LocalStorage],
} as StorageConfig;

const initializeAppFactory = (
  storage: StorageService,
  translationService: TranslationService,
  common: CommonService,
): (() => Promise<void>) => {
  return () =>
    new Promise<void>((resolve) => {
      translationService.setLanguage();
      common.getUniqueDeviceID();
      common.getPlatform();

      storage
        .init()
        .then(async (storage) => {
          const keys = await storage?.keys();
          if (keys?.includes(StorageKey.Token)) {
            const activationCode = storage.get(StorageKey.Token);
            await storage.set(StorageKey.ActivationCode, activationCode);
            await storage.remove(StorageKey.Token);
          }
          resolve();
        })
        .catch(() => resolve());
    });
};

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    IonicModule.forRoot({
      swipeBackEnabled: false,
      backButtonDefaultHref: '/home',
      scrollAssist: false,
      innerHTMLTemplatesEnabled: true,
    }),
    IonicStorageModule.forRoot(storageConfig),
    AppRoutingModule,
    TranslateModule.forRoot({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpBackend],
      },
    }),
    SocketIoModule.forRoot(config),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerImmediately', //  'registerWhenStable:30000'
    }),
    // Custom Modules
    CoreModule,
    SharedModule,
  ],
  providers: [
    provideHttpClient(withInterceptors([intercept, errorIntercept])),
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppFactory,
      deps: [StorageService, TranslationService, CommonService],
      multi: true,
    },
  ],
})
export class AppModule {}
